import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  WEEKDAY_LABELS,
  MONTH_NAMES,
  emitDateValue,
  formatMMDDYYYY,
  getCalendarCells,
  getYearOptions,
  isValidMMDDYYYY,
  maskDateInput,
  parseDateValue,
  VISIBLE_YEAR_COUNT,
  YEAR_OPTION_HEIGHT_PX,
} from '../../utils/dateUtils';

const DEFAULT_INPUT_CLS =
  'w-full px-3.5 py-2.5 rounded-lg bg-gray-50 dark:bg-slate-700/60 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors text-sm';

const SELECT_CLS =
  'px-1.5 py-1 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500';

function YearSelect({ year, years, onChange }) {
  const [listOpen, setListOpen] = useState(false);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const maxHeight = VISIBLE_YEAR_COUNT * YEAR_OPTION_HEIGHT_PX;

  useEffect(() => {
    if (!listOpen) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setListOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [listOpen]);

  useLayoutEffect(() => {
    if (!listOpen || !listRef.current) return;
    const idx = years.indexOf(year);
    if (idx < 0) return;
    const top = Math.max(0, (idx * YEAR_OPTION_HEIGHT_PX) - (maxHeight / 2) + (YEAR_OPTION_HEIGHT_PX / 2));
    listRef.current.scrollTop = top;
  }, [listOpen, year, years, maxHeight]);

  return (
    <div ref={wrapRef} className="relative w-[4.75rem] shrink-0">
      <button
        type="button"
        aria-label="Year"
        aria-expanded={listOpen}
        aria-haspopup="listbox"
        onClick={() => setListOpen((v) => !v)}
        className={`${SELECT_CLS} w-full flex items-center justify-between gap-0.5`}
      >
        <span>{year}</span>
        <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {listOpen && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Select year"
          className="absolute z-[230] left-0 right-0 mt-1 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 shadow-lg overflow-y-auto overscroll-contain [scrollbar-width:thin]"
          style={{ maxHeight }}
        >
          {years.map((y) => (
            <li key={y} role="option" aria-selected={y === year}>
              <button
                type="button"
                onClick={() => {
                  onChange(y);
                  setListOpen(false);
                }}
                className={`w-full px-2 text-left text-xs font-medium transition-colors ${
                  y === year
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-800 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
                style={{ height: YEAR_OPTION_HEIGHT_PX }}
              >
                {y}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Common date picker — display & typing are always MM/DD/YYYY.
 * Calendar supports month/year dropdowns for jumping to older or future dates.
 *
 * @param {string} value - ISO (YYYY-MM-DD) or MM/DD/YYYY
 * @param {(next: string) => void} onChange - emits string in outputFormat
 * @param {'MM/DD/YYYY'|'YYYY-MM-DD'} outputFormat - default MM/DD/YYYY
 */
export default function DatePicker({
  value = '',
  onChange,
  outputFormat = 'MM/DD/YYYY',
  className = DEFAULT_INPUT_CLS,
  placeholder = 'MM/DD/YYYY',
  disabled = false,
  required = false,
  id,
  name,
  'aria-label': ariaLabel = 'Date',
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const [text, setText] = useState(() => formatMMDDYYYY(value));
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [view, setView] = useState(() => {
    const d = parseDateValue(value) || new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const yearOptions = useMemo(() => getYearOptions(), []);

  useEffect(() => {
    setText(formatMMDDYYYY(value));
    const d = parseDateValue(value);
    if (d) setView({ year: d.getFullYear(), month: d.getMonth() });
  }, [value]);

  const commit = useCallback((next) => {
    if (!onChange) return;
    if (!next) {
      onChange('');
      return;
    }
    if (isValidMMDDYYYY(next)) {
      onChange(emitDateValue(next, outputFormat));
    }
  }, [onChange, outputFormat]);

  const selectDay = (day) => {
    const next = formatMMDDYYYY(new Date(view.year, view.month, day));
    setText(next);
    onChange?.(emitDateValue(next, outputFormat));
    setOpen(false);
  };

  const updatePosition = useCallback(() => {
    const anchor = rootRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = 296;
    const height = 360;
    const margin = 8;
    let left = rect.left;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - width - margin);
    }
    let top = rect.bottom + 6;
    if (top + height > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - height - 6);
    }
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onReflow = () => updatePosition();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
      setText(formatMMDDYYYY(value));
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open, value]);

  const selected = parseDateValue(value);
  const today = new Date();
  const cells = getCalendarCells(view.year, view.month);

  const shiftMonth = (delta) => {
    setView((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  return (
    <div ref={rootRef} className="relative w-full min-w-0">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={text}
          onChange={(e) => {
            const next = maskDateInput(e.target.value);
            setText(next);
            if (!next) {
              onChange?.('');
              return;
            }
            if (isValidMMDDYYYY(next)) {
              commit(next);
              const d = parseDateValue(next);
              if (d) setView({ year: d.getFullYear(), month: d.getMonth() });
            }
          }}
          onBlur={() => {
            if (!text) {
              onChange?.('');
              return;
            }
            if (isValidMMDDYYYY(text)) {
              commit(text);
            } else {
              setText(formatMMDDYYYY(value));
            }
          }}
          onFocus={() => {
            if (!disabled) {
              const d = parseDateValue(value) || parseDateValue(text) || new Date();
              setView({ year: d.getFullYear(), month: d.getMonth() });
            }
          }}
          className={`${className} pr-10`}
        />
        <button
          type="button"
          disabled={disabled}
          aria-label="Open calendar"
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 disabled:opacity-40"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Choose date"
          className="fixed z-[220] w-[18.5rem] rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl p-3"
          style={{ top: pos.top, left: pos.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1 mb-2">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 shrink-0"
              aria-label="Previous month"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <select
              aria-label="Month"
              value={view.month}
              onChange={(e) => setView((prev) => ({ ...prev, month: Number(e.target.value) }))}
              className={`${SELECT_CLS} flex-1 min-w-0`}
            >
              {MONTH_NAMES.map((label, idx) => (
                <option key={label} value={idx}>{label}</option>
              ))}
            </select>

            <YearSelect
              year={view.year}
              years={yearOptions.includes(view.year) ? yearOptions : [view.year, ...yearOptions]}
              onChange={(y) => setView((prev) => ({ ...prev, year: y }))}
            />

            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 shrink-0"
              aria-label="Next month"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {WEEKDAY_LABELS.map((d) => (
              <div key={d} className="text-[10px] font-semibold text-center text-gray-400 dark:text-slate-500 py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((day, i) => {
              if (day == null) {
                return <div key={`e-${i}`} className="h-8" />;
              }
              const isSelected = selected
                && selected.getFullYear() === view.year
                && selected.getMonth() === view.month
                && selected.getDate() === day;
              const isToday = today.getFullYear() === view.year
                && today.getMonth() === view.month
                && today.getDate() === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => selectDay(day)}
                  className={`h-8 rounded-lg text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-indigo-600 text-white'
                      : isToday
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                        : 'text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-slate-700">
            <button
              type="button"
              onClick={() => {
                setText('');
                onChange?.('');
                setOpen(false);
              }}
              className="text-xs text-gray-500 hover:text-red-500 dark:text-slate-400"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                const next = formatMMDDYYYY(now);
                setView({ year: now.getFullYear(), month: now.getMonth() });
                setText(next);
                onChange?.(emitDateValue(next, outputFormat));
                setOpen(false);
              }}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400"
            >
              Today
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
